// 盆栽（成長する植物デコ）の 3D 表現。成長段階と樹形パラメータを「形」に翻訳する。
//
// sim（plants.ts）は段階と樹形を純データとして決める。ここはそれを受けて
// 芽→若木→成木→古木の見た目を作る描画専用層。段階差がひと目で分かること・
// 樹形パラメータ（枝の本数/曲がり/茂り）が枝と樹冠の配置に出ることを重視する。
//
// ドット絵テクスチャは作らず低ポリの「形と色」で表現（ピクセレートが効く）。
// 各ビルダーはセル中心原点・地面 y=0 基準の Group を返す。

import * as THREE from 'three';
import type { GrowthStage, TreeForm } from '../sim/plants';

const COLORS = {
  trunk: '#A07A5A',
  trunkOld: '#8A6646',
  leaf: '#9CCB8A',
  leafLight: '#B5DDA0',
  leafDeep: '#7FB874',
  sproutLeaf: '#A8E0A0',
  bedWood: '#D8B89A',
  soil: '#7A5A42',
  flowerViolet: '#C9B8E8',
  flowerPink: '#F0C0D0',
  flowerGold: '#FFD79A',
  glow: '#D8F0C8',
} as const;

function mat(color: string, opts: Partial<THREE.MeshStandardMaterialParameters> = {}): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    roughness: 0.85,
    metalness: 0.0,
    flatShading: true,
    ...opts,
  });
}

function glowMat(color: string, opacity = 1): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color: new THREE.Color(color),
    transparent: opacity < 1,
    opacity,
  });
}

/** 樹形 branches（0..1）を枝の本数（2..5）に量子化。 */
function branchCount(form: TreeForm): number {
  return 2 + Math.round(form.branches * 3); // 2,3,4,5
}

/** 樹冠の塊を 1 つ作る（茂り lush で大きさを変える）。 */
function crownBlob(radius: number, color: string): THREE.Mesh {
  return new THREE.Mesh(new THREE.IcosahedronGeometry(radius, 0), mat(color));
}

/**
 * 芽（sprout）: 土から出た小さな双葉。「植えたばかり」がひと目で分かる最小表現。
 */
function buildSprout(): THREE.Group {
  const g = new THREE.Group();
  // 小さな土の盛り上がり
  const mound = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2), mat(COLORS.soil));
  mound.scale.y = 0.5;
  g.add(mound);
  // 細い茎
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.018, 0.12, 5), mat(COLORS.leafDeep));
  stem.position.y = 0.09;
  g.add(stem);
  // 双葉（左右に開いた小さな葉）
  for (const side of [-1, 1]) {
    const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.06, 7, 5), mat(COLORS.sproutLeaf));
    leaf.scale.set(1.2, 0.4, 0.8);
    leaf.position.set(side * 0.06, 0.15, 0);
    leaf.rotation.z = side * 0.5;
    g.add(leaf);
  }
  return g;
}

/**
 * 樹木（若木/成木/古木）を樹形から組む。段階で幹の高さ・樹冠量・発光が変わる。
 *
 * @param stage 'sapling' | 'mature' | 'ancient'
 * @param form  樹形パラメータ（枝の本数/曲がり/茂り/仕立て）
 */
function buildTree(stage: GrowthStage, form: TreeForm): THREE.Group {
  const g = new THREE.Group();

  // 段階パラメータ: 高さ・幹の太さ・樹冠半径・本数倍率。
  const sizing = {
    sapling: { trunkH: 0.34, trunkR: 0.05, crownR: 0.18, scale: 1.0 },
    mature: { trunkH: 0.52, trunkR: 0.08, crownR: 0.26, scale: 1.0 },
    ancient: { trunkH: 0.62, trunkR: 0.12, crownR: 0.32, scale: 1.0 },
  }[stage === 'sapling' ? 'sapling' : stage === 'mature' ? 'mature' : 'ancient'];

  const trunkColor = stage === 'ancient' ? COLORS.trunkOld : COLORS.trunk;

  // 幹（古木はやや傾けて「年経た」風情を出す。曲がり bend で傾きを足す）。
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(sizing.trunkR * 0.7, sizing.trunkR, sizing.trunkH, stage === 'ancient' ? 7 : 6),
    mat(trunkColor),
  );
  trunk.position.y = sizing.trunkH / 2;
  const lean = (form.bend - 0.5) * 0.5 + (stage === 'ancient' ? 0.12 : 0);
  trunk.rotation.z = lean;
  g.add(trunk);

  // 古木は根張りを足す（象徴感）。
  if (stage === 'ancient') {
    for (let i = 0; i < 4; i++) {
      const a = i * (Math.PI / 2) + 0.4;
      const root = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.05, 0.16, 5), mat(trunkColor));
      root.position.set(Math.cos(a) * 0.1, 0.04, Math.sin(a) * 0.1);
      root.rotation.set(Math.PI / 2.4, 0, a);
      g.add(root);
    }
  }

  // 枝＋先端の樹冠。本数は branches、配置は bend、塊の大きさは lush に従う。
  const n = branchCount(form);
  const topY = sizing.trunkH + Math.sin(lean) * 0; // 幹のてっぺん付近
  const crownBaseR = sizing.crownR * (0.7 + form.lush * 0.6);

  // 中央の主樹冠
  const main = crownBlob(crownBaseR, COLORS.leaf);
  main.position.set(Math.sin(lean) * sizing.trunkH * 0.5, topY + crownBaseR * 0.5, 0);
  g.add(main);

  // 周囲の枝先樹冠を放射状に。bend が大きいほど角度が散る。
  for (let i = 0; i < n; i++) {
    const ang = (i / n) * Math.PI * 2 + form.bend * 1.7;
    const spread = 0.16 + form.bend * 0.14;
    const r = crownBaseR * (0.5 + (i % 2) * 0.18);
    const lightColor = i % 2 === 0 ? COLORS.leafLight : COLORS.leafDeep;
    const blob = crownBlob(r, lightColor);
    blob.position.set(
      Math.cos(ang) * spread + Math.sin(lean) * sizing.trunkH * 0.5,
      topY + crownBaseR * 0.5 + Math.sin(i * 1.3) * 0.06,
      Math.sin(ang) * spread,
    );
    g.add(blob);

    // 細い枝（幹のてっぺんから各樹冠へ）
    if (stage !== 'sapling') {
      const branch = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.02, spread + 0.1, 4), mat(trunkColor));
      branch.position.set(Math.cos(ang) * spread * 0.5, topY * 0.92, Math.sin(ang) * spread * 0.5);
      branch.rotation.set(Math.PI / 2 - 0.6, 0, ang + Math.PI / 2);
      g.add(branch);
    }
  }

  // 仕立て trained を花/光で示す: 伸ばし(>0)＝若葉のきらめき、整え(<0)＝静かな実。
  if (form.trained > 0.3) {
    for (let i = 0; i < 3; i++) {
      const a = i * 2.3 + form.bend;
      const spark = new THREE.Mesh(new THREE.SphereGeometry(0.03, 6, 5), glowMat(COLORS.glow));
      spark.position.set(Math.cos(a) * crownBaseR, topY + crownBaseR * 0.7, Math.sin(a) * crownBaseR);
      g.add(spark);
    }
  } else if (form.trained < -0.3) {
    for (let i = 0; i < 3; i++) {
      const a = i * 2.3 + form.bend;
      const berry = new THREE.Mesh(new THREE.SphereGeometry(0.025, 6, 5), mat(COLORS.flowerPink));
      berry.position.set(Math.cos(a) * crownBaseR * 0.8, topY + crownBaseR * 0.3, Math.sin(a) * crownBaseR * 0.8);
      g.add(berry);
    }
  }

  // 古木はうっすら発光する樹冠を重ねて「象徴」感を出す。
  if (stage === 'ancient') {
    const halo = new THREE.Mesh(new THREE.IcosahedronGeometry(crownBaseR * 1.25, 0), glowMat(COLORS.glow, 0.18));
    halo.position.copy(main.position);
    g.add(halo);
  }

  return g;
}

/**
 * 宇宙すみれの花壇（violet_bed）。M4 で簡易 2 段階の成長を持つ:
 *  - 芽/若木（芽吹き）: つぼみ少なめ。
 *  - 成木/古木（満開）: 花が増え、古木では淡く発光。
 *
 * 花壇自体（木枠＋土）は据え置きで、上に咲く花の量と発光で段階差を出す。
 */
function buildVioletBed(stage: GrowthStage): THREE.Group {
  const g = new THREE.Group();
  const box = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.18, 0.6), mat(COLORS.bedWood));
  box.position.y = 0.09;
  g.add(box);
  const soil = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.06, 0.52), mat(COLORS.soil));
  soil.position.y = 0.2;
  g.add(soil);

  // 段階で咲く花の数を増やす（芽:2 / 若木:4 / 成木:6 / 古木:8）。
  const counts: Record<GrowthStage, number> = { sprout: 2, sapling: 4, mature: 6, ancient: 8 };
  const count = counts[stage];
  const bloomy = stage === 'mature' || stage === 'ancient';
  const flowerColors = [COLORS.flowerViolet, COLORS.flowerPink, COLORS.flowerGold];
  for (let i = 0; i < count; i++) {
    const a = i * 2.39963; // 黄金角で散らす
    const rad = 0.08 + (i % 3) * 0.06;
    const h = bloomy ? 0.3 : 0.26;
    const f = new THREE.Mesh(new THREE.SphereGeometry(bloomy ? 0.07 : 0.05, 6, 5), mat(flowerColors[i % 3]));
    f.position.set(Math.cos(a) * rad, h, Math.sin(a) * rad);
    g.add(f);
  }

  // 古木相当（最も育った花壇）はうっすら発光する花霞を足す。
  if (stage === 'ancient') {
    const haze = new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 6), glowMat(COLORS.flowerViolet, 0.12));
    haze.position.y = 0.32;
    g.add(haze);
  }

  return g;
}

/**
 * 植物デコの 3D 表現を成長段階・樹形から作る。
 *
 * @param decoId 'sapling' | 'violet_bed'（PLANT_DECO_IDS）
 * @param stage  現在の成長段階
 * @param form   樹形パラメータ
 * @returns セル中心原点・地面 y=0 基準の Group（未対応 ID は null）
 */
export function buildPlantMesh(decoId: string, stage: GrowthStage, form: TreeForm): THREE.Group | null {
  if (decoId === 'sapling') {
    return stage === 'sprout' ? buildSprout() : buildTree(stage, form);
  }
  if (decoId === 'violet_bed') {
    return buildVioletBed(stage);
  }
  return null;
}
